import Button from "components/global/Button";
import Logo from "components/global/Logo";
import Spacer from "components/global/Spacer";
import ToolsList from "./ToolsList";
import { socialIcons } from "constants/post";
import PlusIcon from "icons/PlusIcon";
import DraftEditor from "./Editor";
import mediaIcon from "assets/icons/media_icon.svg";
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import ImageCaptionIcon from "icons/ImageCaptionIcon";
import { miniAppsAxiosInstance } from "api";
import {
  Redirect,
  Route,
  Switch,
  useLocation,
  useHistory,
} from "react-router-dom";
import RocketLaunchIcon from "icons/RocketLaunchIcon";
import { showAlert } from "utils/utils";
import { CompositeDecorator, ContentState, EditorState } from "draft-js";
import PencilIcon from "icons/PencilIcon";
import Dropdown from "./Dropdown";
import AiWand from "icons/AiWand";
import axios from "axios";
import CopyIcon from "icons/CopyIcon";
import CalenderIcon from "icons/CalenderIcon";
import sendIcon from "assets/icons/sendIcon2.svg";
import Loader from "components/global/Loader";
import ConfirmModal from "components/global/ConfirmModal";
import {
  Wrapper,
  InputWrapper,
  Divider,
  findHashtags,
  HashtagSpan,
  findLinks,
  LinkSpan,
  findMentions,
  MentionSpan,
  typeCaption,
} from "./MiniAppsStyles";
import LightBulbIcon from "icons/LightBulbIcon";
import AlertBox from "components/global/AlertBox";

const toolsData = [
  {
    title: "Quick post",
    description:
      "Generate instant, on-brand captions with quick post - fast, easy, tailored just for you.",
    btnText: "Quick post",
    btnIcon: <RocketLaunchIcon color="#fff" />,
  },
  {
    title: "Image to Caption",
    description:
      "Upload your brand image, and optionally add your handle or summary for a custom caption.",
    btnText: "Generate Caption",
    btnIcon: <ImageCaptionIcon color="#fff" />,
  },
  {
    title: "Post Ideas",
    description:
      "Create post ideas and transform them into captivating captions for your brand.",
    btnText: "Generate Ideas",
    btnIcon: <LightBulbIcon color="#fff" />,
  },
  {
    title: "Post caption",
    description:
      "Tell us what you need, and we'll craft the perfect caption just for you!",
    btnText: "Generate Caption",
    btnIcon: <PencilIcon color="#fff" />,
  },
];

const tone = [
  { value: "Happy", label: "😀 Happy" },
  { value: "Friendly", label: "😊 Friendly" },
  { value: "Professional", label: "💼 Professional" },
];

const pexelBaseUrl = `https://api.pexels.com/v1/`;

const withToolCountCheck =
  (apiCallback, add = false) =>
  async (...args) => {
    const useToolCount = localStorage.getItem("used-tool-count");
    const count = useToolCount ? parseInt(useToolCount) : 0;

    if (count >= 5) {
      document.querySelector(`#promptModal.confirmModal`).classList.add("show");
      return;
    }

    if (!add)
      localStorage.setItem("used-tool-count", JSON.stringify(count + 1));
    return await apiCallback(...args);
  };

const initialState = {
  brandSummary: "",
  igUsername: "",
  generatedCaption: "",
  toneValue: tone[0].value,
  lengthValue: "Standard",
  showMore: false,
  pov: "First person (Singular)",
  postIdea: "",
  includedContent: "None",
  imageUrl: "",
  generatedPostIdeas: [],
  error: false,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "resetState":
      return initialState;
    default:
      return { ...state, [action.type]: action.payload };
  }
};

const MiniAppsHome = () => {
  const path = "/mini-apps";
  const inputRef = useRef(null);
  const { search, pathname } = useLocation();
  const history = useHistory();
  const activeToolName = new URLSearchParams(search).get("tool");
  const [state, dispatch] = useReducer(reducer, initialState);

  const compositeDecorator = new CompositeDecorator([
    {
      strategy: findHashtags,
      component: HashtagSpan,
    },
    {
      strategy: findLinks,
      component: LinkSpan,
    },
    {
      strategy: findMentions,
      component: MentionSpan,
    },
  ]);

  const [editorState, setEditorState] = useState(
    EditorState.createEmpty(compositeDecorator)
  );
  const [loading, setLoading] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const activeTool = useMemo(() => {
    return (
      toolsData.find(
        (tool) =>
          tool.title.toLowerCase() === activeToolName?.split("-").join(" ")
      ) ?? toolsData[0]
    );
  }, [activeToolName]);

  const handleUpload = () => {
    inputRef.current.click();
  };

  const resetStates = () => {
    dispatch({ type: "resetState" });
  };

  const processImg = async (tempFileList) => {
    setIsUploading(true);
    try {
      const url = `/v1/image_processor/upload_and_process`;

      const res = await miniAppsAxiosInstance.post(
        url,
        {
          ids: "01",
          images: tempFileList[0],
        },
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      dispatch({ type: "imageUrl", payload: res?.data?.data?.[0]?.url });
      setIsUploading(false);
    } catch (error) {
      showAlert({ type: "danger", msg: "An error occurred. Try again!" });
      setIsUploading(false);
    }
  };

  async function processUrl(url) {
    let tempFileList = [];

    try {
      const response = await axios.get(url, { responseType: "blob" });
      const blob = response.data;

      const fileName = "image.jpg";
      const mimeType = "image/jpeg";
      const imageFile = new File([blob], fileName, { type: mimeType });

      tempFileList.push(imageFile);

      processImg(tempFileList);
    } catch (error) {
      showAlert({ type: "danger", msg: "Network error, please try again" });
    }
  }

  const getContentIdeas = withToolCountCheck(async (generateMore = false) => {
    const data = {
      max_tokens: 8000,
      temperature: 0.8,
      is_streaming: false,
      timeout: 60,
      top_p: 0.9,
      llm_name: "azure/gpt-4.1-mini",
      requires_vision: false,
      username: state.igUsername?.startsWith("@")
        ? state.igUsername.slice(1)
        : state.igUsername,
      brand_description: state.brandSummary ?? "",
    };

    setLoading(true);
    try {
      const res = await miniAppsAxiosInstance.post(
        "/v1/mini_apps/generate_content_ideas",
        data
      );

      const postIdeas = res?.data?.data?.generation?.new_ideas;
      if (postIdeas) {
        dispatch({
          type: "generatedPostIdeas",
          payload: generateMore
            ? [...state.generatedPostIdeas, ...postIdeas]
            : postIdeas,
        });
        setLoading(false);
        if (!generateMore) {
          history.push(`${path}/post-ideas-result`);
        }
      } else {
        showAlert({
          type: "danger",
          msg: "An error occurred. Try again!",
        });
        dispatch({ type: "error", payload: true });
        setLoading(false);
      }
    } catch (error) {
      showAlert({
        type: "danger",
        msg: "An error occurred. Try again!",
      });
      dispatch({ type: "error", payload: true });
      setLoading(false);
    }
  });

  const handleQuickPost = withToolCountCheck(async () => {
    const url = "/v1/mini_apps/generate_quick_post";
    const reqBody = {
      max_tokens: 8000,
      temperature: 0.9,
      is_streaming: false,
      timeout: 60,
      top_p: 0.9,
      llm_name: "azure/gpt-4.1-mini",
      requires_vision: false,
      username: state.igUsername?.startsWith("@")
        ? state.igUsername.slice(1)
        : state.igUsername,
      brand_description: state.brandSummary,
    };
    setLoading(true);
    try {
      const res = await miniAppsAxiosInstance.post(url, reqBody);
      const caption = res?.data?.data?.generation?.caption;
      let imageKeyword = res?.data?.data?.generation?.image_keyword;
      if (imageKeyword) {
        const res = await axios.get(
          `${pexelBaseUrl}search?query=${imageKeyword}&orientation=square&per_page=1`,
          {
            headers: {
              Authorization: `${process.env.REACT_APP_PEXEL_API_KEY}`,
            },
          }
        );

        const imageData = res?.data?.photos?.[0];
        processUrl(imageData?.src?.original);
      }
      if (caption) {
        dispatch({ type: "generatedCaption", payload: caption });
        setLoading(false);
        history.push(`${path}/result`);
      } else {
        showAlert({
          type: "danger",
          msg: "An error occurred. Try again!",
        });
        setLoading(false);
      }
    } catch (error) {
      showAlert({
        type: "danger",
        msg: "An error occurred. Try again!",
      });
      setLoading(false);
    }
  });

  const handleGenerateCaption = withToolCountCheck(async () => {
    const url = "/v1/mini_apps/promotional_caption";
    const reqBody = {
      max_tokens: 8000,
      temperature: 0.2,
      is_streaming: false,
      timeout: 60,
      top_p: 0,
      llm_name: "azure/gpt-4.1-mini",
      requires_vision: false,
      length: state.lengthValue,
      tone: state.toneValue,
      included_content: state.includedContent,
      purpose: "Informational",
      point_of_view: state.pov,
      post_idea: state.postIdea ?? "",
      username: state.igUsername?.startsWith("@")
        ? state.igUsername.slice(1)
        : state.igUsername ?? "",
      brand_description: state.brandSummary ?? "",
      social_media_channel: "Instagram",
    };

    setLoading(true);
    try {
      const res = await miniAppsAxiosInstance.post(url, reqBody);
      const caption = res?.data?.data?.generation?.caption;
      if (caption) {
        dispatch({ type: "generatedCaption", payload: caption });
        setLoading(false);
        history.push(`${path}/result?tool=post-captions`);
      } else {
        showAlert({
          type: "danger",
          msg: "An error occurred. Try again!",
        });
        setLoading(false);
      }
    } catch (error) {
      showAlert({
        type: "danger",
        msg: "An error occurred. Try again!",
      });
      setLoading(false);
    }
  });

  const generateImageCaption = withToolCountCheck(async () => {
    const url = "/v1/mini_apps/generate_image_captions";
    const reqBody = {
      max_tokens: 8000,
      temperature: 0.4,
      is_streaming: false,
      timeout: 60,
      top_p: 0.4,
      llm_name: "azure/gpt-4.1-mini",
      requires_vision: false,
      username: state.igUsername?.startsWith("@")
        ? state.igUsername.slice(1)
        : state.igUsername ?? "",
      brand_description: state.brandSummary ?? "",
      image_url: state.imageUrl ?? "",
    };

    setLoading(true);
    try {
      const res = await miniAppsAxiosInstance.post(url, reqBody);
      const caption = res?.data?.data?.generation?.caption;
      if (caption) {
        dispatch({ type: "generatedCaption", payload: caption });
        setLoading(false);
        history.push(`${path}/result${search}`);
      } else {
        showAlert({
          type: "danger",
          msg: "An error occurred. Try again!",
        });
        setLoading(false);
      }
    } catch (error) {
      showAlert({
        type: "danger",
        msg: "An error occurred. Try again!",
      });
      setLoading(false);
    }
  });

  const onChange = useCallback((newState) => {
    if (newState) {
      setEditorState(newState);
    }
  }, []);

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(state.generatedCaption);
    showAlert({
      msg: "Caption copied to clipboard",
      type: "success",
    });
  };

  const handleRegister = () => {
    localStorage.setItem("free_gift_caption", state.generatedCaption);
    history.push("/sign-up?return_to=/dashboard/posts/calendar-view?post_action=create_post");
  };

  const handlePromptUser = () => {
    document.querySelector(`#promptModal.confirmModal`).classList.add("show");
  };

  const handleClickPostIdea = (post) => {
    resetStates();
    dispatch({ type: "postIdea", payload: post });
    history.push(`/mini-apps/tool?tool=post-caption`);
  };

  useEffect(() => {
    if (state.generatedCaption?.length && pathname === `${path}/result`) {
      typeCaption(state.generatedCaption, onChange, compositeDecorator);
    }
  }, [state.generatedCaption, pathname, onChange]);

  return (
    <Wrapper>
      <AlertBox />
      <ConfirmModal
        id="promptModal"
        title="Unlock Full Access to Our AI Tools!"
        description="Sign up or log in to unlock everything you need to create, plan, and engage with your audience seamlessly."
        actionText="Create account"
        callback={handleRegister}
        onCancelCallback={() => {
          localStorage.setItem("free_gift_caption", state.generatedCaption);
          history.push("/sign-in?return_to=/dashboard/posts/calendar-view?post_action=create_post");
        }}
        noHeaderImages
        onCancelText="Login"
        type="info"
        allowClickOutside
      />

      <div className="flexRow alignCenter justifySpaceBetween header">
        <div className="flexRow alignCenter">
          <Logo />
          <Spacer x={1} />
          <p>PostPaddy</p>
        </div>

        <div className="flexRow alignCenter">
          <Button
            className="borderedGrey"
            text="Login"
            height="36px"
            fontSize="14px"
            width="80px"
            onClick={() => history.push("/sign-in")}
          />
          <Spacer x={1.5} />
          <Button
            text="Sign up"
            height="36px"
            fontSize="14px"
            width="100px"
            onClick={() => history.push("/sign-up")}
          />
        </div>
      </div>

      <div className="flexRow alignCenter justifySpaceBetween contentBody">
        <div className="postAreaContainer">
          <div>
            <h4>{activeTool?.title}</h4>
            <p className="l3 colorBodyText">{activeTool?.description}</p>
          </div>

          <Switch>
            <Route
              exact
              path={`${path}`}
              component={() => <Redirect to={`${path}/tool`} />}
            />

            <Route exact path={`${path}/tool`}>
              <div className="postArea">
                {/* Image caption section */}
                {activeTool?.title === "Image to Caption" && (
                  <>
                    {state.imageUrl ? (
                      <div className="imageContainerPreview">
                        <img src={state.imageUrl} alt="img" />
                      </div>
                    ) : (
                      <div
                        className="uploadArea cursorPointer"
                        onClick={handleUpload}
                      >
                        <input
                          type="file"
                          accept="image/*, video/*"
                          multiple
                          ref={inputRef}
                          onChange={(e) => processImg(e.target.files)}
                        />
                        <div className="l3 textCenter flexCol alignCenter justifyCenter">
                          {isUploading ? (
                            <Loader type="spinner" />
                          ) : (
                            <>
                              <img src={mediaIcon} alt="" />
                              <p>Drop media</p>
                              <p>
                                or{" "}
                                <span className="colorSecondary">select</span>{" "}
                                to upload
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="l3">
                        Additional information{" "}
                        <span className="colorGrey6">(Optional)</span>
                      </label>
                      <InputWrapper>
                        <img
                          src={socialIcons.circle.instagram}
                          alt="instagram"
                          className="instagram-icon"
                        />
                        <input
                          type="text"
                          placeholder="Enter IG Business handle"
                          value={state.igUsername}
                          onChange={(e) =>
                            dispatch({
                              type: "igUsername",
                              payload: e.target.value,
                            })
                          }
                        />
                      </InputWrapper>
                    </div>

                    <div className="footer">
                      <Button
                        text={activeTool?.btnText}
                        height="40px"
                        fontSize="14px"
                        iconComponent={activeTool?.btnIcon}
                        onClick={generateImageCaption}
                        disabled={loading || !state.imageUrl}
                        loading={loading}
                      />
                    </div>
                  </>
                )}

                {/* Post caption section */}
                {["Post caption"].includes(activeTool?.title) && (
                  <>
                    <div className="flexCol fullWidth">
                      <div className="l3">Post idea</div>
                      <textarea
                        placeholder="Enter topic, e.g. Fun activities to organize"
                        value={state.postIdea}
                        onChange={(e) =>
                          dispatch({
                            type: "postIdea",
                            payload: e.target.value,
                          })
                        }
                        className="textarea fullWidth"
                      />
                    </div>
                    <div>
                      <label>
                        Instagram Business handle{" "}
                        <span className="colorBodyText">(Optional)</span>
                      </label>
                      <InputWrapper>
                        <img
                          src={socialIcons.circle.instagram}
                          alt="instagram"
                          className="instagram-icon"
                        />
                        <input
                          type="text"
                          placeholder="Enter IG Business handle"
                          value={state.igUsername}
                          onChange={(e) =>
                            dispatch({
                              type: "igUsername",
                              payload: e.target.value,
                            })
                          }
                        />
                      </InputWrapper>
                    </div>
                    <div>
                      <label>
                        About brand{" "}
                        <span className="colorBodyText">(Optional)</span>
                      </label>
                      <InputWrapper>
                        <input
                          type="text"
                          placeholder="Enter brand summary"
                          value={state.brandSummary}
                          onChange={(e) =>
                            dispatch({
                              type: "brandSummary",
                              payload: e.target.value,
                            })
                          }
                          className="noImage"
                        />
                      </InputWrapper>
                    </div>
                    <div>
                      <label>Tone of voice</label>
                      <Dropdown
                        options={tone}
                        value={state.toneValue}
                        onChange={(e) =>
                          dispatch({ type: "toneValue", payload: e.value })
                        }
                        placeholder="Select tone of voice"
                      />
                    </div>
                    <div>
                      <label>Length</label>
                      <Dropdown
                        options={["Short", "Standard", "Long"].map((opt) => ({
                          value: opt,
                          label: opt,
                        }))}
                        value={state.lengthValue}
                        onChange={(e) =>
                          dispatch({ type: "lengthValue", payload: e.value })
                        }
                        placeholder="Select length"
                      />
                    </div>
                    <div className="flexRow alignCenter">
                      <Divider className="fullWidth" />
                      <span
                        onClick={() => setShowMore((prev) => !prev)}
                        className="showMore l3 textNoWrap cursorPointer"
                      >
                        {showMore ? "Show less" : "Show advanced options"}
                      </span>
                      <Divider className="fullWidth" />
                    </div>

                    {showMore && (
                      <>
                        <div>
                          <label>Included content</label>
                          <Dropdown
                            options={["None", "Call to Action (CTA)"].map(
                              (opt) => ({
                                value: opt,
                                label: opt,
                              })
                            )}
                            value={state.includedContent}
                            onChange={(e) =>
                              dispatch({
                                type: "includedContent",
                                payload: e.value,
                              })
                            }
                            placeholder="Select content"
                          />
                        </div>

                        <div>
                          <label>Point of view</label>
                          <Dropdown
                            options={[
                              "First person (Singular)",
                              "First person (Plural)",
                              "Second person",
                            ].map((opt) => ({
                              value: opt,
                              label: opt,
                            }))}
                            value={state.pov}
                            onChange={(e) =>
                              dispatch({ type: "pov", payload: e.value })
                            }
                            placeholder="Select point of view"
                          />
                        </div>
                      </>
                    )}

                    <div className="footer">
                      <Button
                        text={activeTool?.btnText}
                        height="40px"
                        fontSize="14px"
                        iconComponent={activeTool?.btnIcon}
                        onClick={handleGenerateCaption}
                        disabled={loading || !state.postIdea}
                        loading={loading}
                        fullWidth
                      />
                    </div>
                  </>
                )}

                {["Quick post"].includes(activeTool?.title) && (
                  <>
                    <div>
                      <label className="l3">Additional information</label>
                      <InputWrapper>
                        <img
                          src={socialIcons.circle.instagram}
                          alt="instagram"
                          className="instagram-icon"
                        />
                        <input
                          type="text"
                          placeholder="Enter IG Business handle"
                          value={state.igUsername}
                          onChange={(e) =>
                            dispatch({
                              type: "igUsername",
                              payload: e.target.value,
                            })
                          }
                        />
                      </InputWrapper>
                    </div>

                    <textarea
                      placeholder="Enter brand summary (Optional)"
                      value={state.brandSummary}
                      onChange={(e) =>
                        dispatch({
                          type: "brandSummary",
                          payload: e.target.value,
                        })
                      }
                      className="textarea"
                    />

                    <div className="footer">
                      <Button
                        text={activeTool?.btnText}
                        height="40px"
                        fontSize="14px"
                        iconComponent={activeTool?.btnIcon}
                        onClick={handleQuickPost}
                        disabled={!state.igUsername || loading}
                        loading={loading}
                      />
                    </div>
                  </>
                )}

                {["Post Ideas"].includes(activeTool?.title) && (
                  <>
                    {state.error && (
                      <div className="errorCard">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M14.4001 8.00156C14.4001 11.5362 11.5347 14.4016 8.0001 14.4016C4.46548 14.4016 1.6001 11.5362 1.6001 8.00156C1.6001 4.46694 4.46548 1.60156 8.0001 1.60156C11.5347 1.60156 14.4001 4.46694 14.4001 8.00156ZM8.0001 4.00156C8.33147 4.00156 8.6001 4.27019 8.6001 4.60156V8.20156C8.6001 8.53293 8.33147 8.80156 8.0001 8.80156C7.66873 8.80156 7.4001 8.53293 7.4001 8.20156V4.60156C7.4001 4.27019 7.66873 4.00156 8.0001 4.00156ZM8.0001 12.0016C8.44192 12.0016 8.8001 11.6434 8.8001 11.2016C8.8001 10.7597 8.44192 10.4016 8.0001 10.4016C7.55827 10.4016 7.2001 10.7597 7.2001 11.2016C7.2001 11.6434 7.55827 12.0016 8.0001 12.0016Z"
                            fill="#FD0403"
                          />
                        </svg>
                        <p>
                          We can't access your Instagram page right now. Please
                          provide your brand details.
                        </p>
                      </div>
                    )}
                    <InputWrapper>
                      <img
                        src={socialIcons.circle.instagram}
                        alt="instagram"
                        className="instagram-icon"
                      />
                      <input
                        type="text"
                        placeholder="Enter IG Business handle"
                        value={state.igUsername}
                        onChange={(e) =>
                          dispatch({
                            type: "igUsername",
                            payload: e.target.value,
                          })
                        }
                      />
                    </InputWrapper>

                    <textarea
                      placeholder="Enter brand summary (Optional)"
                      value={state.brandSummary}
                      onChange={(e) =>
                        dispatch({
                          type: "brandSummary",
                          payload: e.target.value,
                        })
                      }
                      className="textarea"
                    />

                    <div className="footer">
                      <Button
                        text={activeTool?.btnText}
                        height="40px"
                        fontSize="14px"
                        iconComponent={activeTool?.btnIcon}
                        onClick={() => getContentIdeas(false)}
                        disabled={loading || !state.igUsername}
                        loading={loading}
                      />
                    </div>
                  </>
                )}
              </div>
            </Route>

            {/* this route below is where result for all the tools are displayed excepts post ideas */}
            <Route path={`${path}/result`}>
              <>
                <div className="postArea result">
                  <div>
                    <p className="l3">Select social media channels</p>
                  </div>
                  <div className="socialsList">
                    {[
                      "facebook",
                      "instagram",
                      "linkedin",
                      "twitter",
                      "youtube",
                      "tiktok",
                    ].map((social, i) => (
                      <div
                        className="socialIconWrapper"
                        key={i}
                        onClick={handlePromptUser}
                      >
                        <img src={socialIcons.circle[social]} alt={social} />
                      </div>
                    ))}
                    <div className="socialIconWrapper">
                      <PlusIcon />
                    </div>
                  </div>
                  <Spacer y={2} />
                  <div className="postBox">
                    <DraftEditor
                      onChange={onChange}
                      editorState={editorState}
                    />

                    {isUploading && (
                      <div className="imageContainer">
                        <Loader type="spinner" />
                      </div>
                    )}
                    {state.imageUrl && (
                      <div className="imageContainer">
                        <img src={state.imageUrl} alt="" />
                      </div>
                    )}

                    <div className="actionCenter">
                      <Button
                        text="Copy"
                        height="36px"
                        fontSize="14px"
                        className="borderedGrey resultBtn"
                        iconComponent={<CopyIcon />}
                        disabled={!editorState.getCurrentContent().hasText()}
                        onClick={handleCopyToClipboard}
                      />

                      <Button
                        text="Post now"
                        height="36px"
                        fontSize="14px"
                        className="borderedGrey resultBtn"
                        icon={sendIcon}
                        onClick={handlePromptUser}
                        disabled={!editorState.getCurrentContent().hasText()}
                      />

                      <Button
                        text="Schedule"
                        height="36px"
                        fontSize="14px"
                        className="borderedGrey resultBtn"
                        iconComponent={<CalenderIcon size={16} />}
                        onClick={handlePromptUser}
                        disabled={!editorState.getCurrentContent().hasText()}
                      />
                    </div>
                  </div>
                </div>

                {activeTool?.title === "Image to Caption" ? (
                  <div
                    className="flexRow alignCenter justifyCenter generateMoreBtn"
                    onClick={() => {
                      resetStates();
                      withToolCountCheck(
                        () => history.push(`${path}/tool${search}`),
                        true
                      )();
                    }}
                  >
                    <ImageCaptionIcon color="#000" />
                    Try with another image
                  </div>
                ) : (
                  <div
                    className="flexRow alignCenter justifyCenter generateMoreBtn"
                    onClick={() => {
                      resetStates();
                      withToolCountCheck(
                        () => history.push(`${path}/tool${search}`),
                        true
                      )();
                    }}
                  >
                    <PencilIcon color="#000" />
                    Generate more captions
                  </div>
                )}
              </>
            </Route>

            {/* this route below is where result for post ideas are displayed */}
            <Route path={`${path}/post-ideas-result`}>
              <div>
                <div className="postArea result">
                  {state.generatedPostIdeas &&
                    state.generatedPostIdeas.length > 0 &&
                    state.generatedPostIdeas.map((caption, i) => (
                      <div key={i} onClick={() => handleClickPostIdea(caption)}>
                        <div className="postIdeaCard">{caption}</div>
                      </div>
                    ))}

                  <div className="footer flexRow alignCenter justifyCenter">
                    <Button
                      text="Generate more"
                      height="40px"
                      fontSize="14px"
                      className="noBorder"
                      color="var(--secondary)"
                      iconComponent={<AiWand />}
                      onClick={() => getContentIdeas(true)}
                      loading={loading}
                    />
                  </div>
                </div>
              </div>
            </Route>
          </Switch>
        </div>
        <div>
          <ToolsList />
        </div>
      </div>
    </Wrapper>
  );
};

export default MiniAppsHome;
